import { faCross } from "@fortawesome/free-solid-svg-icons";
import Button from "components/common/button/Button";
import Form from "components/common/form/Form";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import Input from "components/common/form/inputs/Input";
import TelInput from "components/common/form/inputs/TelInput";
import Textarea from "components/common/form/inputs/Textarea";
import UsernameInput from "components/common/form/inputs/UsernameInput";
import { FormEvent, useState } from "react";
import { personListInfo } from "types/person-types";
import { DropdownInputPropsType, InputCostumeErrorType } from "types/form-types";
import { PersonType } from "types/person-types";
import { DataSourceRequestType } from "types/datalist-types";
import { PersonFilterInput, PersonListInfoQueryVariables, PersonSortInput } from "types/graphql";
import DateInput from "components/common/form/inputs/date/DateInput";
import MultiSelectInput, { MultiSelectInputPropsType } from "components/common/form/inputs/MultiSelectInput";
import ColorInput from "@/components/common/form/inputs/ColorInput";
import DateRangeInput from "@/components/common/form/inputs/date/DateRangeInput";

type IdNameType = {
  id: number,
  name: string
}

const DemoFormModelDefault = {
  text: "",
  email: "",
  username: "",
  phone: "",
  tel: '',
  customError: '',
  textarea: '',
  checkbox: false,
  dropdown: undefined as string | undefined,
  dropdownModel: undefined as IdNameType | undefined,
  dropdownPerson: undefined as PersonType | undefined,
  date: undefined as Date | undefined,
  dateRange: [] as Date[],
  multiselect: [] as string[],
  select: '',
  color: ''
};

type DemoFormModelType = typeof DemoFormModelDefault;

const DemoForm = () => {
  let [model, setModel] = useState<DemoFormModelType>({
    ...DemoFormModelDefault,
  });

  // #region configs
  const dropdownConfig: DropdownInputPropsType<string, never> = {
    getItemId: i => i,
    required: true,
    dataSource: {
      data: [
        'aaaaaa',
        'bbbbbb',
        'cccccc'
      ]
    }, 
    view: {
      inputView: i=> i
    },
    label: 'Dropdown',
    onChange: i => changeModelValue({dropdown: i})
  };

  const multiSelectOptions: MultiSelectInputPropsType<string> = {
    ...dropdownConfig,
    onChange: i => changeModelValue({multiselect: i ? [...i] : []}),
    value: model.multiselect,
    label: 'MultiSelect'
  } as MultiSelectInputPropsType<string>

  const dropdownModelConfig: DropdownInputPropsType<IdNameType, any> = {
    getItemId: i => i.id,
    dataSource: {
      data: searchText => {
        const list = [{
          id: 1,
          name: 'test1'
        }, {
          id: 2,
          name: 'test2'
        },{
          id: 3,
          name: 'test3'
        }, {
          id: 4,
          name: 'test4'
        }];
        return searchText ? list.filter(i => i.name.indexOf(searchText!) > -1) : list;
      }
    }, 
    view: {
      inputView: i=> i?.name
    },
    label: 'Dropdown',
    onChange: i => changeModelValue({dropdownModel: i})
  };

  
  const dropdownPersonConfig: DropdownInputPropsType<PersonType, PersonFilterInput, PersonSortInput> = {
    getItemId: i => i.id,
    required: true,
    dataSource: {
      request: async (params: DataSourceRequestType<PersonFilterInput, PersonSortInput>) => {
        let variables: PersonListInfoQueryVariables = {
            take: params.take
        };

        if(params.searchText) {
          variables.where = {
            or: [
              {
                name: {
                  contains: params.searchText
                }
              },
              {
                note: {
                  contains: params.searchText
                }
              },
              {
                contactInfo: {
                  some: {
                    value: {
                      contains: params.searchText
                    }
                  }
                }
              }
            ]
          }
        }

        const data = await personListInfo(variables);
        return data.data.items;
      },
    }, 
    view: {
      inputView: i=> i?.name,
      dropdownView: i => {
        return <div key={`dropdown-i-${i.id}`}>
          <div><b>{i.name}</b></div>
          {i.contactInfo.map(c => {
            return <div key={`dropdown-i-${i.id}-${c.id}`}><i>{c.type} - {c.value}</i></div>
          })}
        </div>
      }
    },
    label: 'Dropdown',
    onChange: i => changeModelValue({dropdownPerson: i})
  };
  // #endregion configs

  const changeModelValue = (m: Partial<DemoFormModelType>) => {
    const cm = {
      ...model,
      ...m
    };
    setModel(cm);
  };

  const customError: InputCostumeErrorType<string>[] = [{
    message: 'Custom error',
    name: 'Custom error',
    validate: (v, i) => {
      return i.value === 'no baby no' ? true : false
    }
  }]

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    console.log(model);
  };

  return (
    <div className="sm:w-max px-4 lg:px-0">
      <Form onSubmit={onSubmit} name="demo-form">
        <fieldset>
          <p className="text-sx">Dropdown</p>
          <DropdownInput {...dropdownConfig} value={model.dropdown} />
          <DropdownInput {...dropdownModelConfig} value={model.dropdownModel} />
          <DropdownInput {...dropdownPersonConfig} value={model.dropdownPerson} />
        </fieldset>
        <fieldset>
          <p className="text-sx">Test inputs</p>
          <Input
            type="text"
            label="text"
            name="text"
            minLength={4}
            required
            maxLength={50}
            value={model.text}
            onChange={(e) => changeModelValue({ "text": e })}
          />
          <Input
            type="email"
            label="email"
            name="email"
            minLength={4}
            maxLength={50}
            value={model.email}
            onChange={(e) => changeModelValue({ "email": e })}
            suffix={{ icon: faCross, name: "demo-from-email-suffix", onClick: () => changeModelValue({ "email": '' }) }}
          />
          <UsernameInput
            label="username"
            name="username"
            minLength={4}
            maxLength={50}
            value={model.username}
            onChange={(e) => changeModelValue({ "username": e })}
            suffix={{ icon: faCross, name: "demo-from-username-suffix" }}
          />
        </fieldset>
        <fieldset>
          <p className="text-sx">Number inputs</p>
          <TelInput
            label="Tel input"
            name="tel"
            value={model.tel}
            onChange={e => changeModelValue({ tel: e })}
          />
          <DateInput
            label="Date"
            value={model.date}
            onChange={e => changeModelValue({ date: e })}
          />

          <DateRangeInput
            label="Date range"
            value={model.dateRange}
            onChange={e => {
              changeModelValue({ dateRange: e })
              console.log(e);
            }}
          />

          <Input
            label="Custom error"
            name="customError"
            value={model.customError}
            customErrors={customError}
            onChange={e => changeModelValue({ customError: e })}
          />
          <Textarea
            label="Textarea"
            value={model.textarea}
            onChange={e => changeModelValue({ textarea: e })}
          />
          <CheckboxInput
            label="checkbox"
            value={model.checkbox}
            onChange={e => changeModelValue({ checkbox: e })}
          />
          <ColorInput
            label="Color"
            value={model.color}
            onChange={e => changeModelValue({ color: e })}
          />
        </fieldset>
        <fieldset>
          <p className="text-sx">multiselect</p>
          <MultiSelectInput {...multiSelectOptions} value={model.multiselect} />
        </fieldset>
        <fieldset className="form-actions">
          <Button type="submit" title="submit" className="primary">
            <span>Submit</span>
          </Button>
          <Button type="reset" title="submit" className="">
            <span>Reset</span>
          </Button>
        </fieldset>
      </Form>
    </div>
  );
};

export default DemoForm;
